import { useState, useEffect, useCallback, useRef } from 'react';
import { roomApi } from '../services/api';

/**
 * Custom hook for real-time slug validation and availability checking
 * @param {string} slug - The slug to validate
 * @param {string} roomId - Optional room ID (for updates, to exclude current room)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500)
 * @returns {Object} Validation state
 */
export function useSlugValidation(slug, roomId = null, debounceMs = 500) {
  const [isChecking, setIsChecking] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  const debounceTimer = useRef(null);
  const lastCheckedSlug = useRef('');

  const validateSlug = useCallback(async (slugToValidate) => {
    // Don't check if slug is empty
    if (!slugToValidate || slugToValidate.trim() === '') {
      setError(null);
      setIsValid(true);
      setIsAvailable(true);
      setSuggestion(null);
      return;
    }

    // Don't re-check the same slug
    if (slugToValidate === lastCheckedSlug.current) {
      return;
    }

    lastCheckedSlug.current = slugToValidate;
    setIsChecking(true);
    setError(null);
    setSuggestion(null);

    try {
      const result = await roomApi.checkSlugAvailable(slugToValidate, roomId);

      if (result.available) {
        setIsValid(true);
        setIsAvailable(true);
        setError(null);
        setSuggestion(null);
      } else {
        setIsValid(!result.error?.includes('Slug must be')); // Format error vs taken error
        setIsAvailable(false);
        setError(result.error);
        setSuggestion(result.suggestion);
      }
    } catch (err) {
      console.error('Slug validation error:', err);
      setError('Failed to check slug availability');
      setIsValid(false);
      setIsAvailable(false);
    } finally {
      setIsChecking(false);
    }
  }, [roomId]);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      validateSlug(slug);
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [slug, validateSlug, debounceMs]);

  return {
    isChecking,
    isValid,
    isAvailable,
    error,
    suggestion,
  };
}

export default useSlugValidation;
